<template>
    <div class="container-fluid content-wrapper">
        <!-- The page heading, as the wrapper's first child: content-heading stretches itself
             over the wrapper's padding, so nested in another element it spilled outside the
             page margins. -->
        <div class="content-heading">
            <!-- The heading is the report's name, not its question: the name defaults to the
                 question when saved, so it is labelled to keep the two apart. -->
            <template v-if="report && !renaming">
                <div>
                    <small>Saved AI report</small>
                    {{ report.name }}
                </div>
                <!-- Only the person who saved it can rename it; anyone can run it. -->
                <button
                    v-if="report.isMine"
                    class="btn btn-link btn-sm p-0 ms-2 text-muted"
                    title="Rename this report (the question and settings stay the same)"
                    @click="startRename">
                    <b-icon-pencil-square />
                </button>
            </template>
            <div v-else-if="report" class="d-flex align-items-center gap-2 flex-grow-1 me-3">
                <input
                    ref="nameInput"
                    v-model="draftName"
                    class="form-control"
                    placeholder="Report name"
                    aria-label="Report name"
                    style="max-width: 28rem"
                    @keydown.enter.prevent="commitRename"
                    @keydown.esc.prevent="renaming = false" />
                <p-button variant="primary" size="sm" :disabled="!draftName.trim()" @click="commitRename">Save</p-button>
                <p-button variant="outline-secondary" size="sm" @click="renaming = false">Cancel</p-button>
            </div>
            <span v-else>AI Report</span>
            <!-- This report's own history, on its own page. -->
            <router-link v-if="report" class="btn btn-outline-secondary btn-sm ms-auto" :to="`/reports/ai/${report.id}/activity`">Activity</router-link>
        </div>
        <div class="small mb-2"><router-link to="/reports/ai">← AI Reports</router-link></div>

        <p-card v-if="loaded && !report" title="This report no longer exists" class="mt-2">
            <p class="mb-0">It may have been deleted by the person who saved it.</p>
        </p-card>

        <template v-if="report">
            <!-- What was asked is history: it stays as typed when the report was saved. What
                 runs comes from the settings below, so that is what the second line describes. -->
            <p-card title="What was asked" class="mt-2">
                <p v-if="report.question" class="mb-1"><strong>Asked:</strong> “{{ report.question }}”</p>
                <p v-if="restatement" class="mb-1"><strong>Runs as:</strong> <span class="fst-italic">{{ restatement }}</span></p>
                <p class="small text-muted mb-0">
                    Saved by {{ report.ownerName || 'someone else' }}.
                    It runs for the location you have selected.
                    <span v-if="report.isMine">Renaming the report does not change the question or its settings; change the settings below to change what it runs.</span>
                    <span v-else>Only the person who saved it can change it.</span>
                </p>
            </p-card>

            <p-card title="Settings" class="mt-3">
                <TransactionsParameters
                    v-if="tool"
                    v-model="params"
                    :tool="tool"
                    :errors="errors"
                    :ambiguities="ambiguities"
                    :busy="isBusy"
                    :dirty="dirty"
                    @run="runReport(1)" />
                <div v-if="report.isMine && settingsChanged" class="mt-2 d-flex align-items-center gap-2">
                    <p-button variant="outline-primary" size="sm" :is-busy="isSaving" :disabled="dirty" @click="saveSettings">
                        Save these settings to the report
                    </p-button>
                    <span class="small text-muted">What is on screen ran with settings that differ from the saved ones.</span>
                </div>
            </p-card>

            <ServerReportResults
                v-if="applied"
                :title="title"
                :rows="rows"
                :columns="columns"
                :total="total"
                :page="page"
                :per-page="perPage"
                :busy="isBusy"
                @page="runReport($event)"
                @per-page="changePerPage($event)"
                @sort="sort($event)"
                @export="exportReport($event)" />
        </template>
    </div>
</template>

<script>
// A report saved through a server report tool. Anyone can run it, for the location they
// have selected; only its owner can rename it or save new settings to it. Running never
// calls the model - the settings were understood when it was saved.
import { useAppStateStore } from '@/stores/appState';
import { useAiReportsStore } from '@/stores/aiReports';
import * as reportAgentApi from '@/services/reportAgentApi';
import { sameSettings } from '@/services/reportSettings';
import TransactionsParameters from '@/views/Reports/Agent/TransactionsParameters.vue';
import ServerReportResults from '@/views/Reports/Agent/ServerReportResults.vue';

const PER_PAGE = 25;

export default {
    name: 'ServerReportView',
    components: { TransactionsParameters, ServerReportResults },
    props: { id: { type: String, required: true } },
    data() {
        return {
            loaded: false,
            tool: null,
            params: {},
            applied: null,
            restatement: '',
            errors: {},
            ambiguities: [],
            rows: [],
            columns: [],
            total: 0,
            page: 1,
            perPage: PER_PAGE,
            sortBy: null,
            sortDesc: false,
            isBusy: false,
            isSaving: false,
            renaming: false,
            draftName: '',
        };
    },
    computed: {
        store() {
            return useAiReportsStore();
        },
        report() {
            return this.store.getById(this.id) || null;
        },
        locationId() {
            return useAppStateStore().getSelectedLocationId;
        },
        title() {
            const name = this.tool ? this.tool.name : 'Report';
            return `${name} (${this.total} ${this.total === 1 ? 'row' : 'rows'})`;
        },
        // Edited but not run: what is on screen came from the previous settings.
        dirty() {
            return !!this.applied && !sameSettings(this.applied, this.params);
        },
        // Ran with settings other than the saved ones.
        settingsChanged() {
            return !!this.applied && !!this.report && !sameSettings(this.applied, this.report.parameters);
        },
    },
    // No watcher on the location: changing it in the header sends the app to /home, so a
    // re-run here would only log a Run nobody asked for on the way out.
    async created() {
        // Always reloaded, so a report renamed or deleted elsewhere shows as it is now.
        const [tools] = await Promise.all([reportAgentApi.getTools().catch(() => []), this.store.load()]);
        this.loaded = true;
        if (!this.report)
            return;

        this.tool = tools.find(x => x.id === this.report.toolId) || null;
        this.params = JSON.parse(JSON.stringify(this.report.parameters));
        await this.runReport(1);
    },
    methods: {
        async runReport(page) {
            this.isBusy = true;
            try {
                const override = sameSettings(this.params, this.report.parameters) ? null : this.params;
                const data = await reportAgentApi.runSaved(this.report.id, {
                    locationId: this.locationId,
                    overrideParameters: override,
                    currentPage: page,
                    perPage: this.perPage,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc,
                });
                this.rows = data.rows;
                this.columns = data.columns;
                this.total = data.totalRows;
                this.restatement = data.restatement;
                this.page = page;
                this.errors = {};
                this.ambiguities = [];
                this.applied = JSON.parse(JSON.stringify(this.params));
            } catch (e) {
                // The global handler already said what is wrong; show it against each setting too.
                const validation = e.response && e.response.data && e.response.data.validation;
                if (validation) {
                    this.errors = validation.errors || {};
                    this.ambiguities = validation.ambiguities || [];
                }
            } finally {
                this.isBusy = false;
            }
        },
        // A new page size starts again from the first page.
        changePerPage(perPage) {
            this.perPage = perPage;
            this.runReport(1);
        },
        sort({ sortBy, sortDesc }) {
            this.sortBy = sortBy;
            this.sortDesc = sortDesc;
            this.runReport(1);
        },
        async exportReport(format) {
            try {
                // The report's id puts the export in this report's history.
                await reportAgentApi.exportReport({
                    toolId: this.report.toolId,
                    parameters: this.applied,
                    locationId: this.locationId,
                    format,
                    reportDefinitionId: this.report.id,
                });
            } catch {
                // The global handler shows the server's message (for example, PDF over its row limit).
            }
        },
        async saveSettings() {
            this.isSaving = true;
            try {
                await this.store.update(this.report.id, { parameters: this.applied }, this.locationId);
                this.params = JSON.parse(JSON.stringify(this.report.parameters));
                this.applied = JSON.parse(JSON.stringify(this.report.parameters));
                this.$appSuccess && this.$appSuccess(`Settings saved to "${this.report.name}".`);
            } catch {
                // The global handler shows why.
            } finally {
                this.isSaving = false;
            }
        },
        startRename() {
            this.draftName = this.report.name;
            this.renaming = true;
            this.$nextTick(() => this.$refs.nameInput && this.$refs.nameInput.focus());
        },
        async commitRename() {
            const name = this.draftName.trim();
            if (!name)
                return;

            try {
                await this.store.update(this.report.id, { name }, this.locationId);
                this.renaming = false;
                this.$appSuccess && this.$appSuccess(`Report renamed to "${name}".`);
            } catch {
                // The global handler shows why.
            }
        },
    },
};
</script>
